import { useCallback, useEffect, useRef } from 'react';
import { rowActionFor, rowActionsIn } from '../TagManager.utils';
import { ModelCorrelation } from '../types';

// `anchor` is where focus goes after a batch: the bulk bar and the clicked row are both gone by then,
// and leaving focus adrift drops the user next to the toast, where Tab walks off into the page.
type FocusIntent = { slot: number } | { id: number } | { anchor: true };

interface UseRowFocusParams {
  correlations: ModelCorrelation[];
  selectAllRef: React.RefObject<HTMLInputElement>;
}

/**
 * Row actions are addressed through the DOM (see TagManager.utils) because focus has to be restored
 * after a refetch, when the element that was clicked may no longer exist. An intent is recorded before
 * the request and consumed once the new rows render.
 */
export default function useRowFocus({ correlations, selectAllRef }: UseRowFocusParams) {
  const rootRef = useRef<HTMLDivElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const pendingFocusRef = useRef<FocusIntent | undefined>(undefined);

  // A disabled master checkbox (nothing left to select) can't take focus, so fall back to Add.
  const anchor = useCallback(() => (
    (selectAllRef.current?.disabled ? addButtonRef.current : selectAllRef.current) ?? addButtonRef.current
  ), [selectAllRef]);

  const recordRowIntent = useCallback((slot: number) => {
    pendingFocusRef.current = { slot };
  }, []);

  const recordAnchorIntent = useCallback(() => {
    pendingFocusRef.current = { anchor: true };
  }, []);

  const clearIntent = useCallback(() => {
    pendingFocusRef.current = undefined;
  }, []);

  const onUndo = useCallback((correlation: ModelCorrelation) => {
    pendingFocusRef.current = { id: correlation.id };
  }, []);

  const onFailure = useCallback((correlation: ModelCorrelation) => {
    clearIntent();
    window.requestAnimationFrame(() => rowActionFor(rootRef.current, correlation.id)?.focus());
  }, [clearIntent]);

  // For actions with no refetch to wait on, so there is no intent to consume. Deferred a frame
  // because the element being left behind (e.g. the bulk bar) unmounts on the same commit.
  const focusAnchor = useCallback(() => {
    clearIntent();
    window.requestAnimationFrame(() => anchor()?.focus());
  }, [anchor, clearIntent]);

  useEffect(() => {
    const intent = pendingFocusRef.current;
    if (!intent) return;

    pendingFocusRef.current = undefined;

    let target: HTMLElement | null | undefined;

    if ('anchor' in intent) target = anchor();
    else if ('id' in intent) target = rowActionFor(rootRef.current, intent.id);
    else target = rowActionsIn(rootRef.current)[intent.slot];

    (target ?? addButtonRef.current)?.focus();
  }, [correlations]);

  return {
    addButtonRef,
    focusAnchor,
    onBulkFailure: focusAnchor,
    onFailure,
    onUndo,
    recordAnchorIntent,
    recordRowIntent,
    rootRef,
  };
}
